// src/pages/TeamPerformanceReportPage.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, query, getDocs, orderBy, Timestamp, where } from 'firebase/firestore';
import { intervalToDuration } from 'date-fns';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import styles from '../styles/TeamPerformancePage.module.css'; 

// --- Helper Function Definitions ---
const parseDateSafely = (dateValue) => {
    if (!dateValue) return null;
    try {
        if (dateValue instanceof Timestamp) { return dateValue.toDate(); }
        if (typeof dateValue === 'string') {
            const dateString = dateValue.length === 10 && !dateValue.includes('T') ? dateValue + 'T00:00:00Z' : dateValue;
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                const parts = dateValue.split('-');
                if (parts.length === 3) {
                    const year = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const day = parseInt(parts[2], 10);
                    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                        const utcDate = new Date(Date.UTC(year, month, day));
                         if (!isNaN(utcDate.getTime())) return utcDate;
                    }
                }
                console.warn("Invalid date string for parseDateSafely:", dateValue); return null;
            }
            return date;
        }
        if (dateValue instanceof Date && !isNaN(dateValue)) { return dateValue; }
        console.warn("Unknown or invalid date format for parseDateSafely:", dateValue); return null;
    } catch (error) { console.error("Error parsing date in parseDateSafely:", error, dateValue); return null; }
};

const isTaskOverdue = (task) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const endDate = parseDateSafely(task.endDate);
    const statusLower = task.status?.toLowerCase();
    if (!endDate || statusLower === 'completed' || statusLower === 'cancelled' || endDate >= today) return false;
    return true;
};

const isDateInRange = (dateFieldValue, rangeStart, rangeEnd) => {
    if (rangeStart === null && rangeEnd === null) return !!dateFieldValue;
    if (!dateFieldValue) return false;
    const dateToTest = parseDateSafely(dateFieldValue);
    if (!dateToTest) return false;
    let inRange = true;
    if (rangeStart && dateToTest < rangeStart) inRange = false;
    if (rangeEnd && dateToTest > rangeEnd) inRange = false;
    return inRange;
};

const formatAverageDuration = (averageMilliseconds) => {
    if (averageMilliseconds === null || averageMilliseconds < 0 || isNaN(averageMilliseconds)) return 'N/A';
    if (averageMilliseconds === 0) return 'Instantly';
    const endMs = Math.max(0, averageMilliseconds);
    const duration = intervalToDuration({ start: 0, end: endMs });
    const parts = [];
    if (duration.days && duration.days > 0) parts.push(`${duration.days}d`);
    if (duration.hours && duration.hours > 0) parts.push(`${duration.hours}h`);
    if (parts.length < 2 && duration.minutes && duration.minutes > 0) parts.push(`${duration.minutes}m`);
    if (parts.length === 0) {
        if (duration.seconds && duration.seconds > 0) {
            parts.push(`${duration.seconds}s`);
        } else if (averageMilliseconds > 0) {
             return '< 1s';
        } else {
            return '0s';
        }
    }
    if (parts.length === 0 && averageMilliseconds > 0) return '< 1m';
    if (parts.length === 0) return 'N/A';
    return parts.slice(0, 2).join(' ');
};
// --- End Helper Function Definitions ---

const managerAccessRoles = ['Manager', 'Accounts Manager'];


const TeamPerformanceReportPage = () => {
    const { currentUser, userRole, loading: authLoading } = useAuth();
    const [allTasks, setAllTasks] = useState([]);
    const [allEmployees, setAllEmployees] = useState([]); // This will store non-trainee employees
    const [loadingData, setLoadingData] = useState(true);
    const [error, setError] = useState('');

    const [achievementsDateFilter, setAchievementsDateFilter] = useState('today');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    useEffect(() => {
        if (authLoading) return;
        if (!currentUser) {
            if (isMounted.current) {
                setError("Authentication required to view this page.");
                setLoadingData(false);
            }
            return;
        }
        if (userRole && !managerAccessRoles.includes(userRole)) {
            if (isMounted.current) {
                setError("Access Denied: This page is for managers only.");
                setLoadingData(false);
            }
            return;
        }

        const fetchData = async () => {
            if (!isMounted.current) return;
            setLoadingData(true);
            setError('');
            try {
                const usersCol = collection(db, 'users');
                const usersQuery = query(usersCol, orderBy('name', 'asc'));
                const usersSnapshot = await getDocs(usersQuery);
                if (!isMounted.current) return;
                const fetchedEmployees = usersSnapshot.docs
                    .map(doc => ({
                        id: doc.id,
                        name: doc.data().name || doc.data().email,
                        role: doc.data().role || 'Unknown',
                    }))
                    .filter(emp => emp.role?.toLowerCase() !== 'trainee'); // Exclude trainees
                setAllEmployees(fetchedEmployees);

                const tasksCol = collection(db, 'tasks');
                const tasksQuery = query(tasksCol, orderBy('createdAt', 'desc'));
                const tasksSnapshot = await getDocs(tasksQuery);
                if (!isMounted.current) return;
                const fetchedTasks = tasksSnapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        ...data,
                        startDate: parseDateSafely(data.startDate),
                        endDate: parseDateSafely(data.endDate),
                        createdAt: parseDateSafely(data.createdAt),
                        updatedAt: parseDateSafely(data.updatedAt),
                        completedAt: parseDateSafely(data.completedAt),
                        cancelledAt: parseDateSafely(data.cancelledAt),
                        acknowledgedBy: data.acknowledgedBy || {},
                        assignedToUid: data.assignedToUid || (Array.isArray(data.assignedTo) && data.assignedTo.length > 0 ? data.assignedTo[0] : null)
                    };
                });
                setAllTasks(fetchedTasks);

            } catch (err) {
                console.error("Error fetching team performance data:", err);
                if (isMounted.current) setError("Failed to load team performance data. Check console for Firebase index errors.");
            } finally {
                if (isMounted.current) setLoadingData(false);
            }
        };
        fetchData();
    }, [currentUser, userRole, authLoading]);

    const achievementDateRange = useMemo(() => {
        const today = new Date();
        let start = new Date();
        let end = new Date();
        end.setHours(23, 59, 59, 999);

        switch (achievementsDateFilter) {
            case 'today': start.setHours(0, 0, 0, 0); break;
            case 'yesterday':
                start = new Date(today); start.setDate(today.getDate() - 1); start.setHours(0, 0, 0, 0);
                end = new Date(today); end.setDate(today.getDate() - 1); end.setHours(23, 59, 59, 999);
                break;
            case 'last7':
                start = new Date(today); start.setDate(today.getDate() - 6); start.setHours(0, 0, 0, 0);
                break;
            case 'last28':
                start = new Date(today); start.setDate(today.getDate() - 27); start.setHours(0, 0, 0, 0);
                break;
            case 'custom':
                if (customStartDate && customEndDate) {
                    start = parseDateSafely(customStartDate + "T00:00:00");
                    end = parseDateSafely(customEndDate + "T23:59:59.999");
                    if (!start || !end || end < start) return { start: null, end: null, isValid: false, filterType: 'custom' };
                } else return { start: null, end: null, isValid: false, filterType: 'custom' };
                break;
            case 'maximum': default: return { start: null, end: null, isValid: true, filterType: 'maximum' };
        }
        return { start, end, isValid: true, filterType: achievementsDateFilter };
    }, [achievementsDateFilter, customStartDate, customEndDate]);

    const employeeStats = useMemo(() => {
        if (authLoading || loadingData || !allTasks.length || !allEmployees.length || (achievementsDateFilter === 'custom' && !achievementDateRange.isValid)) {
            return [];
        }
        const { start: rangeStart, end: rangeEnd, filterType } = achievementDateRange;
        const statsMap = new Map();

        allEmployees.forEach(emp => { // Already filtered to non-trainees
            statsMap.set(emp.id, {
                employeeName: emp.name,
                employeeRole: emp.role,
                stats: {
                    created: 0, assignedAndCompleted: 0, companyApproved: 0, customerApproved: 0,
                    assignedAndLate: 0, currentlyAssigned: 0,
                    totalAckTime: 0, ackTasksCount: 0,
                    totalCompletionTime: 0, completedTasksAfterAckCount: 0,
                    performanceScore: 0 
                }
            });
        });

        allTasks.forEach(task => {
            const createdAtDate = parseDateSafely(task.createdAt);
            const assignedToUids = Array.isArray(task.assignedTo) ? task.assignedTo : (task.assignedToUid ? [task.assignedToUid] : []);

            if (task.createdBy && statsMap.has(task.createdBy) && isDateInRange(createdAtDate, rangeStart, rangeEnd)) {
                statsMap.get(task.createdBy).stats.created++;
            }
            if (task.companyApprovedBy && statsMap.has(task.companyApprovedBy) && isDateInRange(parseDateSafely(task.companyApprovedAt), rangeStart, rangeEnd)) {
                statsMap.get(task.companyApprovedBy).stats.companyApproved++;
            }
            if (task.customerApprovedBy && statsMap.has(task.customerApprovedBy) && isDateInRange(parseDateSafely(task.customerApprovedAt), rangeStart, rangeEnd)) {
                statsMap.get(task.customerApprovedBy).stats.customerApproved++;
            }

            assignedToUids.forEach(assigneeId => {
                if (statsMap.has(assigneeId)) { 
                    const employeeData = statsMap.get(assigneeId);
                    if (task.status !== 'Completed' && task.status !== 'Cancelled') {
                        if (filterType === 'maximum' || isDateInRange(createdAtDate, rangeStart, rangeEnd)) {
                            employeeData.stats.currentlyAssigned++;
                        }
                    }
                    const ackTimestampObject = task.acknowledgedBy && task.acknowledgedBy[assigneeId];
                    const ackDate = parseDateSafely(ackTimestampObject);
                    const completedDate = parseDateSafely(task.completedAt);

                    if (createdAtDate && ackDate && isDateInRange(ackDate, rangeStart, rangeEnd)) {
                        const diff = ackDate.getTime() - createdAtDate.getTime();
                        if (diff >= 0) {
                            employeeData.stats.totalAckTime += diff;
                            employeeData.stats.ackTasksCount++;
                        }
                    }
                    if (ackDate && completedDate && task.status === 'Completed' && isDateInRange(completedDate, rangeStart, rangeEnd)) {
                        const diff = completedDate.getTime() - ackDate.getTime();
                        if (diff >= 0) {
                            employeeData.stats.totalCompletionTime += diff;
                            employeeData.stats.completedTasksAfterAckCount++;
                        }
                    }
                    if (task.status === 'Completed' && isDateInRange(completedDate, rangeStart, rangeEnd)) {
                        employeeData.stats.assignedAndCompleted++;
                    }
                    if (isTaskOverdue(task) && isDateInRange(task.endDate, rangeStart, rangeEnd)) {
                        employeeData.stats.assignedAndLate++;
                    }
                }
            });
        });

        const result = [];
        statsMap.forEach((value, key) => {
            value.stats.avgAckTime = value.stats.ackTasksCount > 0 ? value.stats.totalAckTime / value.stats.ackTasksCount : null;
            value.stats.avgCompletionTime = value.stats.completedTasksAfterAckCount > 0 ? value.stats.totalCompletionTime / value.stats.completedTasksAfterAckCount : null;
            
            value.stats.performanceScore =
                (value.stats.assignedAndCompleted * 10) +
                (value.stats.created * 2) +
                (value.stats.companyApproved * 3) +
                (value.stats.customerApproved * 3) -
                (value.stats.assignedAndLate * 5);
            
            if (value.stats.avgAckTime !== null && value.stats.ackTasksCount > 2) {
                if (value.stats.avgAckTime < 24 * 60 * 60 * 1000) value.stats.performanceScore += 5;
            }
            if (value.stats.avgCompletionTime !== null && value.stats.completedTasksAfterAckCount > 2) {
                if (value.stats.avgCompletionTime < 3 * 24 * 60 * 60 * 1000) value.stats.performanceScore += 5;
            }

            delete value.stats.totalAckTime;
            delete value.stats.totalCompletionTime;
            result.push({ employeeId: key, ...value });
        });

        return result.sort((a, b) => (b.stats.performanceScore || -Infinity) - (a.stats.performanceScore || -Infinity));
    }, [allTasks, allEmployees, achievementDateRange, achievementsDateFilter, loadingData, authLoading]);

    const topPerformer = useMemo(() => {
        return employeeStats.length > 0 ? employeeStats[0] : null;
    }, [employeeStats]);

    const needsImprovement = useMemo(() => {
        return employeeStats.length > 1 ? employeeStats[employeeStats.length - 1] : 
               (employeeStats.length === 1 ? employeeStats[0] : null);
    }, [employeeStats]);
    
    const todayForDateInput = useMemo(() => {
        const today = new Date();
        return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    }, []);


    if (authLoading || loadingData) { 
        return <div className={styles.loadingIndicator}>Loading Team Performance Data...</div>; 
    }
    if (error) {
        return <div className={`${styles.pageContainer} ${styles.errorMessage}`}>{error}</div>;
    }
    if (!userRole || !managerAccessRoles.includes(userRole)) {
         return <div className={`${styles.pageContainer} ${styles.errorMessage}`}>Access Denied. This page is for managers only.</div>;
    }

    return (
        <div className={styles.pageContainer}>
            <h2 className={styles.pageTitle}>Overall Team Performance</h2>

            <div className={styles.filtersContainer}>
                 <label htmlFor="achievements-date-filter" className={styles.dateFilterLabel}>Stats For:</label>
                <select
                    id="achievements-date-filter"
                    value={achievementsDateFilter}
                    onChange={(e) => setAchievementsDateFilter(e.target.value)}
                    className={styles.filterSelect}
                >
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="last7">Last 7 Days</option>
                    <option value="last28">Last 28 Days</option>
                    <option value="custom">Custom Range</option>
                    <option value="maximum">All Time</option>
                </select>
                {achievementsDateFilter === 'custom' && (
                    <div className={styles.customDateInputs}>
                        <input
                            type="date"
                            aria-label="Custom start date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            className={styles.dateInput}
                            max={customEndDate || todayForDateInput}
                        />
                        <span>to</span>
                        <input
                            type="date"
                            aria-label="Custom end date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            className={styles.dateInput}
                            min={customStartDate || undefined}
                            max={todayForDateInput}
                        />
                    </div>
                )}
            </div>
            {achievementsDateFilter === 'custom' && !achievementDateRange.isValid && (!customStartDate || !customEndDate) && (
                <p className={styles.customDateInfo}>Please select both start and end dates for the custom range.</p>
            )}
            {achievementsDateFilter === 'custom' && !achievementDateRange.isValid && customStartDate && customEndDate && (
                <p className={styles.customDateError}>End date must be on or after start date.</p>
            )}

            {employeeStats.length > 0 && (
                <div className={styles.performanceHighlights}>
                    {topPerformer && (
                        <p className={styles.highlightItem}>
                            🌟 <strong>Top Performer:</strong> {topPerformer.employeeName} 
                            (Score: {topPerformer.stats.performanceScore.toFixed(0)})
                        </p>
                    )}
                    {needsImprovement && topPerformer?.employeeId !== needsImprovement.employeeId && (
                        <p className={styles.highlightItem}>
                            🛠️ <strong>Needs Most Improvement:</strong> {needsImprovement.employeeName} 
                            (Score: {needsImprovement.stats.performanceScore.toFixed(0)})
                        </p>
                    )}
                     {employeeStats.length === 1 && topPerformer && (
                        <p className={styles.highlightItem}>
                           Displaying stats for the only non-trainee employee with data for this period.
                        </p>
                    )}
                </div>
            )}

            <div className={styles.employeeStatsGrid}>
                {employeeStats.length === 0 && !loadingData && 
                    <p className={styles.noDataMessage}>No employee performance data to display for the selected period. Ensure users have roles other than 'Trainee'.</p>
                }
                {employeeStats.map(({ employeeId, employeeName, employeeRole, stats }) => (
                    <div 
                        key={employeeId} 
                        className={`${styles.employeeCard} 
                                    ${topPerformer?.employeeId === employeeId ? styles.topPerformerCard : ''} 
                                    ${needsImprovement?.employeeId === employeeId && topPerformer?.employeeId !== needsImprovement.employeeId ? styles.needsImprovementCard : ''}`}
                    >
                        <h3 className={styles.employeeName}>{employeeName} {/* Correctly using employeeName */}
                            <span className={styles.employeeRole}>({employeeRole})</span>
                            <span className={styles.performanceScore}>(Score: {stats.performanceScore.toFixed(0)})</span>
                        </h3>
                        <div className={styles.statsGrid}>
                            <div className={styles.statItem}><strong>Created:</strong> {stats.created}</div>
                            <div className={styles.statItem}><strong>Assigned:</strong> {stats.currentlyAssigned}</div>
                            <div className={styles.statItem}><strong>Completed (Assigned):</strong> {stats.assignedAndCompleted}</div>
                            <div className={`${styles.statItem} ${stats.assignedAndLate > 0 ? styles.statLate : ''}`}>
                                <strong>Late (Assigned):</strong> {stats.assignedAndLate}
                            </div>
                            <div className={styles.statItem}>
                                <strong>Avg. Ack. Time:</strong> {formatAverageDuration(stats.avgAckTime)}
                                {stats.ackTasksCount > 0 && <small> ({stats.ackTasksCount} tasks)</small>}
                            </div>
                            <div className={styles.statItem}>
                                <strong>Avg. Completion:</strong> {formatAverageDuration(stats.avgCompletionTime)}
                                {stats.completedTasksAfterAckCount > 0 && <small> (from Ack, {stats.completedTasksAfterAckCount} tasks)</small>}
                            </div>
                            <div className={styles.statItem}><strong>Company Approved:</strong> {stats.companyApproved}</div>
                            <div className={styles.statItem}><strong>Customer Approved:</strong> {stats.customerApproved}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TeamPerformanceReportPage;



// // src/pages/StaffPerformanceReportPage.js
// import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
// import { collection, query, getDocs, orderBy, Timestamp, where } from 'firebase/firestore';
// import { intervalToDuration } from 'date-fns';
// import { db } from '../firebase';
// import { useAuth } from '../context/AuthContext';
// import styles from '../styles/StaffPerformanceReportPage.module.css';

// // --- Helper Function Definitions ---
// const parseDateSafely = (dateValue) => {
//     if (!dateValue) return null;
//     try {
//         if (dateValue instanceof Timestamp) { return dateValue.toDate(); }
//         if (typeof dateValue === 'string') {
//             const dateString = dateValue.length === 10 && !dateValue.includes('T') ? dateValue + 'T00:00:00Z' : dateValue;
//             const date = new Date(dateString);
//             if (isNaN(date.getTime())) return null;
//             return date;
//         }
//         if (dateValue instanceof Date && !isNaN(dateValue)) { return dateValue; }
//         return null;
//     } catch (error) { return null; }
// };

// const isTaskOverdue = (task) => {
//     const today = new Date(); today.setHours(0, 0, 0, 0);
//     const endDate = parseDateSafely(task.endDate);
//     const statusLower = task.status?.toLowerCase();
//     if (!endDate || statusLower === 'completed' || statusLower === 'cancelled' || endDate >= today) return false;
//     return true;
// };

// const isDateInRange = (dateFieldValue, rangeStart, rangeEnd) => {
//     if (rangeStart === null && rangeEnd === null) return !!dateFieldValue;
//     if (!dateFieldValue) return false;
//     const dateToTest = parseDateSafely(dateFieldValue);
//     if (!dateToTest) return false;
//     let inRange = true;
//     if (rangeStart && dateToTest < rangeStart) inRange = false;
//     if (rangeEnd && dateToTest > rangeEnd) inRange = false;
//     return inRange;
// };

// const formatAverageDuration = (averageMilliseconds) => {
//     if (averageMilliseconds === null || averageMilliseconds < 0 || isNaN(averageMilliseconds)) return 'N/A';
//     if (averageMilliseconds === 0) return 'Instantly';
//     const endMs = Math.max(0, averageMilliseconds);
//     const duration = intervalToDuration({ start: 0, end: endMs });
//     const parts = [];
//     if (duration.days && duration.days > 0) parts.push(`${duration.days}d`);
//     if (duration.hours && duration.hours > 0) parts.push(`${duration.hours}h`);
//     if (parts.length < 2 && duration.minutes && duration.minutes > 0) parts.push(`${duration.minutes}m`);
//     if (parts.length === 0) {
//         if (duration.seconds && duration.seconds > 0) parts.push(`${duration.seconds}s`);
//         else if (averageMilliseconds > 0) return '< 1s';
//         else return '0s';
//     }
//     if (parts.length === 0 && averageMilliseconds > 0) return '< 1m';
//     if (parts.length === 0) return 'N/A';
//     return parts.slice(0, 2).join(' ');
// };

// // ** ADDED MISSING HELPER FUNCTION **
// const getDateString = (date) => {
//     const year = date.getFullYear();
//     const month = String(date.getMonth() + 1).padStart(2, '0');
//     const day = String(date.getDate()).padStart(2, '0');
//     return `${year}-${month}-${day}`;
// };
// // --- End Helper Function Definitions ---

// const managerAccessRoles = ['Manager', 'Accounts Manager'];

// const StaffPerformanceReportPage = () => {
//     const { userRole, loading: authLoading } = useAuth();
//     const [allTasks, setAllTasks] = useState([]);
//     const [allEmployees, setAllEmployees] = useState([]);
//     const [loadingData, setLoadingData] = useState(true);
//     const [error, setError] = useState('');

//     const [reportDateFilter, setReportDateFilter] = useState('last7');
//     const [customStartDate, setCustomStartDate] = useState('');
//     const [customEndDate, setCustomEndDate] = useState('');

//     const isMounted = useRef(true);

//     useEffect(() => {
//         isMounted.current = true;
//         return () => { isMounted.current = false; };
//     }, []);

//     useEffect(() => {
//         if (authLoading) return;
//         if (userRole && !managerAccessRoles.includes(userRole)) {
//             if (isMounted.current) {
//                 setError("Access Denied: This page is for managers only.");
//                 setLoadingData(false);
//             }
//             return;
//         }

//         const fetchData = async () => {
//             if (!isMounted.current) return;
//             setLoadingData(true);
//             setError('');
//             try {
//                 const usersCol = collection(db, 'users');
//                 const usersQuery = query(usersCol, orderBy('name', 'asc'));
//                 const usersSnapshot = await getDocs(usersQuery);
//                 if (!isMounted.current) return;
//                 const fetchedEmployees = usersSnapshot.docs
//                     .map(doc => ({
//                         id: doc.id,
//                         name: doc.data().name || doc.data().email,
//                         role: doc.data().role || 'Unknown',
//                     }))
//                     .filter(emp => emp.role?.toLowerCase() !== 'trainee');
//                 setAllEmployees(fetchedEmployees);

//                 const tasksCol = collection(db, 'tasks');
//                 const tasksQuery = query(tasksCol, orderBy('createdAt', 'desc'));
//                 const tasksSnapshot = await getDocs(tasksQuery);
//                 if (!isMounted.current) return;
//                 const fetchedTasks = tasksSnapshot.docs.map(doc => ({
//                     id: doc.id, ...doc.data(),
//                     createdAt: parseDateSafely(doc.data().createdAt),
//                     endDate: parseDateSafely(doc.data().endDate),
//                     completedAt: parseDateSafely(doc.data().completedAt),
//                     companyApprovedAt: parseDateSafely(doc.data().companyApprovedAt),
//                     customerApprovedAt: parseDateSafely(doc.data().customerApprovedAt),
//                     acknowledgedBy: doc.data().acknowledgedBy || {},
//                     assignedTo: doc.data().assignedTo || [],
//                 }));
//                 setAllTasks(fetchedTasks);
//             } catch (err) {
//                 console.error("Error fetching report data:", err);
//                 if (isMounted.current) setError("Failed to load report data. Check console for Firebase index errors.");
//             } finally {
//                 if (isMounted.current) setLoadingData(false);
//             }
//         };
//         fetchData();
//     }, [userRole, authLoading]);

//     const reportDateRange = useMemo(() => {
//         const today = new Date();
//         let start = new Date();
//         let end = new Date();
//         end.setHours(23, 59, 59, 999);
//         switch (reportDateFilter) {
//             case 'today': start.setHours(0, 0, 0, 0); break;
//             case 'yesterday':
//                 start = new Date(today); start.setDate(today.getDate() - 1); start.setHours(0, 0, 0, 0);
//                 end = new Date(today); end.setDate(today.getDate() - 1); end.setHours(23, 59, 59, 999);
//                 break;
//             case 'last7':
//                 start = new Date(today); start.setDate(today.getDate() - 6); start.setHours(0, 0, 0, 0);
//                 break;
//             case 'last28':
//                 start = new Date(today); start.setDate(today.getDate() - 27); start.setHours(0, 0, 0, 0);
//                 break;
//             case 'custom':
//                 if (customStartDate && customEndDate) {
//                     start = parseDateSafely(`${customStartDate}T00:00:00`);
//                     end = parseDateSafely(`${customEndDate}T23:59:59`);
//                     if (!start || !end || end < start) return { start: null, end: null, isValid: false };
//                 } else return { start: null, end: null, isValid: false };
//                 break;
//             case 'maximum': default: return { start: null, end: null, isValid: true };
//         }
//         return { start, end, isValid: true };
//     }, [reportDateFilter, customStartDate, customEndDate]);

//     const reportData = useMemo(() => {
//         if (loadingData || !allTasks.length || !allEmployees.length || !reportDateRange.isValid) {
//             return [];
//         }
//         const { start: rangeStart, end: rangeEnd } = reportDateRange;
        
//         return allEmployees.map(employee => {
//             let stats = {
//                 created: 0, assignedAndCompleted: 0, companyApproved: 0, customerApproved: 0,
//                 assignedAndLate: 0, currentlyAssigned: 0,
//                 totalAckTime: 0, ackTasksCount: 0,
//                 totalCompletionTime: 0, completedTasksAfterAckCount: 0,
//             };

//             allTasks.forEach(task => {
//                 const isAssignedToThisEmployee = (task.assignedTo || []).includes(employee.id);
                
//                 if (task.createdBy === employee.id && isDateInRange(task.createdAt, rangeStart, rangeEnd)) {
//                     stats.created++;
//                 }
//                 if (task.companyApprovedBy === employee.id && isDateInRange(task.companyApprovedAt, rangeStart, rangeEnd)) {
//                     stats.companyApproved++;
//                 }
//                 if (task.customerApprovedBy === employee.id && isDateInRange(task.customerApprovedAt, rangeStart, rangeEnd)) {
//                     stats.customerApproved++;
//                 }

//                 if (isAssignedToThisEmployee) {
//                     if (task.status !== 'Completed' && task.status !== 'Cancelled') {
//                         if (reportDateFilter === 'maximum' || isDateInRange(task.createdAt, rangeStart, rangeEnd)) {
//                             stats.currentlyAssigned++;
//                         }
//                     }
//                     const ackDate = task.acknowledgedBy && task.acknowledgedBy[employee.id] ? parseDateSafely(task.acknowledgedBy[employee.id]) : null;
                    
//                     if (task.createdAt && ackDate && isDateInRange(ackDate, rangeStart, rangeEnd)) {
//                         const diff = ackDate.getTime() - task.createdAt.getTime();
//                         if (diff >= 0) {
//                             stats.totalAckTime += diff;
//                             stats.ackTasksCount++;
//                         }
//                     }
//                     if (ackDate && task.completedAt && task.status === 'Completed' && isDateInRange(task.completedAt, rangeStart, rangeEnd)) {
//                         const diff = task.completedAt.getTime() - ackDate.getTime();
//                         if (diff >= 0) {
//                             stats.totalCompletionTime += diff;
//                             stats.completedTasksAfterAckCount++;
//                         }
//                     }
//                     if (task.status === 'Completed' && isDateInRange(task.completedAt, rangeStart, rangeEnd)) {
//                         stats.assignedAndCompleted++;
//                     }
//                     if (isTaskOverdue(task) && isDateInRange(task.endDate, rangeStart, rangeEnd)) {
//                         stats.assignedAndLate++;
//                     }
//                 }
//             });
            
//             stats.avgAckTime = stats.ackTasksCount > 0 ? stats.totalAckTime / stats.ackTasksCount : null;
//             stats.avgCompletionTime = stats.completedTasksAfterAckCount > 0 ? stats.totalCompletionTime / stats.completedTasksAfterAckCount : null;

//             return {
//                 employeeId: employee.id,
//                 employeeName: employee.name,
//                 employeeRole: employee.role,
//                 stats
//             };
//         });
//     }, [allTasks, allEmployees, reportDateRange, loadingData]);

//     const handlePrint = () => window.print();

//     const handleExport = useCallback(() => {
//         if (!reportData.length) return;
    
//         const headers = [
//             "Employee Name", "Role", "Tasks Created", "Currently Assigned", "Completed (as Assignee)", 
//             "Late (as Assignee)", "Company Approvals", "Customer Approvals", 
//             "Avg. Ack Time", "Avg. Completion Time (from Ack)"
//         ];
        
//         const rows = reportData.map(item => [
//             `"${item.employeeName.replace(/"/g, '""')}"`,
//             item.employeeRole,
//             item.stats.created,
//             item.stats.currentlyAssigned,
//             item.stats.assignedAndCompleted,
//             item.stats.assignedAndLate,
//             item.stats.companyApproved,
//             item.stats.customerApproved,
//             formatAverageDuration(item.stats.avgAckTime),
//             formatAverageDuration(item.stats.avgCompletionTime)
//         ].join(','));
    
//         const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows].join("\n");
        
//         const encodedUri = encodeURI(csvContent);
//         const link = document.createElement("a");
//         link.setAttribute("href", encodedUri);
//         link.setAttribute("download", `staff_performance_report_${getDateString(new Date())}.csv`);
//         document.body.appendChild(link);
//         link.click();
//         document.body.removeChild(link);
    
//     }, [reportData]);

//     const todayForDateInput = useMemo(() => getDateString(new Date()), []);

//     if (authLoading || loadingData) { 
//         return <div className={styles.loadingIndicator}>Loading Staff Performance Data...</div>; 
//     }
//     if (error) {
//         return <div className={`${styles.pageContainer} ${styles.errorMessage}`}>{error}</div>;
//     }

//     return (
//         <div className={styles.pageContainer}>
//             <div className={styles.reportHeader}>
//                 <h2 className={styles.pageTitle}>Staff Performance Report</h2>
//                 <div className={styles.actions}>
//                     <button onClick={handlePrint} className={styles.actionButton}>Print</button>
//                     <button onClick={handleExport} className={styles.actionButton} disabled={reportData.length === 0}>Export as CSV</button>
//                 </div>
//             </div>

//             <div className={styles.filtersContainer}>
//                  <label htmlFor="report-date-filter" className={styles.dateFilterLabel}>Report For:</label>
//                 <select id="report-date-filter" value={reportDateFilter} onChange={(e) => setReportDateFilter(e.target.value)} className={styles.filterSelect}>
//                     <option value="today">Today</option>
//                     <option value="yesterday">Yesterday</option>
//                     <option value="last7">Last 7 Days</option>
//                     <option value="last28">Last 28 Days</option>
//                     <option value="custom">Custom Range</option>
//                     <option value="maximum">All Time</option>
//                 </select>
//                 {reportDateFilter === 'custom' && (
//                     <div className={styles.customDateInputs}>
//                         <input type="date" aria-label="Custom start date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className={styles.dateInput} max={customEndDate || todayForDateInput}/>
//                         <span>to</span>
//                         <input type="date" aria-label="Custom end date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className={styles.dateInput} min={customStartDate || undefined} max={todayForDateInput}/>
//                     </div>
//                 )}
//             </div>

//             <div className={styles.reportTableContainer}>
//                 <table className={styles.reportTable}>
//                     <thead>
//                         <tr>
//                             <th>Employee</th>
//                             <th>Role</th>
//                             <th>Created</th>
//                             <th>Assigned</th>
//                             <th>Completed</th>
//                             <th>Late</th>
//                             <th>Company Aprv.</th>
//                             <th>Customer Aprv.</th>
//                             <th>Avg. Ack.</th>
//                             <th>Avg. Comp.</th>
//                         </tr>
//                     </thead>
//                     <tbody>
//                         {reportData.length === 0 && (
//                             <tr>
//                                 <td colSpan="10" className={styles.noDataMessage}>No performance data found for the selected period.</td>
//                             </tr>
//                         )}
//                         {reportData.map(({ employeeId, employeeName, employeeRole, stats }) => (
//                             <tr key={employeeId}>
//                                 <td>{employeeName}</td>
//                                 <td>{employeeRole}</td>
//                                 <td>{stats.created}</td>
//                                 <td>{stats.currentlyAssigned}</td>
//                                 <td>{stats.assignedAndCompleted}</td>
//                                 <td className={stats.assignedAndLate > 0 ? styles.statLate : ''}>{stats.assignedAndLate}</td>
//                                 <td>{stats.companyApproved}</td>
//                                 <td>{stats.customerApproved}</td>
//                                 <td>{formatAverageDuration(stats.avgAckTime)}</td>
//                                 <td>{formatAverageDuration(stats.avgCompletionTime)}</td>
//                             </tr>
//                         ))}
//                     </tbody>
//                 </table>
//             </div>
//         </div>
//     );
// };

// export default StaffPerformanceReportPage;